import { HOOK_MARK } from '@rc-component/form/es/FieldContext';
import { InternalFormInstance, NamePath } from '@rc-component/form/es/interface';
import { Alert, Form, FormInstance, FormItemProps, Radio, RadioChangeEvent, RadioProps } from 'antd';
import debounce from 'debounce-promise';
import React, { useEffect, useMemo, useState } from 'react';
import { ValidationIssue } from 'src/extension-taxonomy/shared/form-modal';
import { useRerender } from 'src/shared/use-rerender';

export function clearErrors(form: FormInstance) {
  const errors = form.getFieldsError();
  if (!errors.some((x) => x.errors.length || x.warnings.length)) {
    return;
  }
  form.setFields(
    errors
      .filter(({ errors, warnings }) => errors.length || warnings.length)
      .map(({ name }) => ({ name, errors: [], validated: false, validating: false, warnings: [] })),
  );
}

export function FormRadio(props: Omit<RadioProps, 'checked'>) {
  const { children, name, value, ...rest } = props;
  const form = Form.useFormInstance();
  const currentValue = Form.useWatch(name, form);
  const onChange = (e: RadioChangeEvent) => {
    if (e.target.checked) {
      form.setFieldValue(name, value);
      // Workaround for https://github.com/react-component/field-form/pull/661 - calling setFieldValue doesn't trigger any validation updates on validated fields
      form.validateFields(Object.keys(form.getFieldsValue({ filter: (meta) => meta != null && meta.validated })));
    }
  };
  return (
    <Radio {...rest} checked={currentValue === value} onChange={onChange}>
      {children}
    </Radio>
  );
}

function resetInitialValue<TData>(form: FormInstance<TData>, name: NamePath, initialValue: unknown) {
  // Reach inside rc-field-form's internals to work around it not respecting the prop's value after construction.
  const internalForm = (form as unknown as InternalFormInstance).getInternalHooks(HOOK_MARK)!;
  const fields = internalForm.getFields();
  const initialValues: Record<NamePath, unknown> = {};
  fields.forEach((field) => (initialValues[field.name] = internalForm.getInitialValue(fields[0].name)));
  initialValues[name] = initialValue;
  internalForm.setInitialValues(initialValues, true);
  // Finally, set the field value to trigger all watchers to rerender
  form.resetFields([name]);
}

/**
 * Like Form.Item from ant.design, except it respects updates to `initialValue`.
 * This has the benefit of simplicity to use where the initial value comes from a backend call, and allows resetting the field to actually work in this case.
 */
export function FormItem<TData>(props: FormItemProps<TData>) {
  const { children, initialValue, ...rest } = props;
  const form = Form.useFormInstance<TData>();
  const rerender = useRerender();
  useEffect(() => {
    // We've got a new initial value - only reset it if the field hasn't been touched by the user.
    if (props.name && !form.isFieldTouched(props.name)) {
      resetInitialValue(form, props.name, initialValue);
      rerender();
    }
  }, [initialValue, props.name]);
  return <Form.Item<TData> {...rest}>{children}</Form.Item>;
}

/**
 * Performs additional async validation when any form item changes.
 * `onValidate()` should return true or undefined for no error, or a ValidationIssue for an error.
 */
export function AdditionalValidationFormItem<TData extends object>(props: {
  readonly onValidate: (values: TData) => Promise<true | ValidationIssue | void>;
  readonly debounceMs?: number;
}) {
  const form = Form.useFormInstance();
  const doValidate = useMemo(
    () =>
      props.debounceMs
        ? debounce((values: TData) => props.onValidate(values), props.debounceMs, {
            leading: true,
          })
        : (values: TData) => props.onValidate(values),
    [],
  );
  const errors = form.getFieldError('additionalValidation');
  useEffect(() => {
    form.setFields([{ name: 'additionalValidation', touched: true }]);
  }, []);

  const values = Form.useWatch<TData>((v) => v);
  const [oldValues, setOldValues] = useState<unknown[]>([]);
  // useEffect() isn't good enough here is it won't accept a variable number of dependencies.
  if (haveValuesChanged(oldValues, values ? Object.values(values) : [])) {
    setTimeout(async () => {
      setOldValues(values ? Object.values(values) : []);
      const result = await doValidate(form.getFieldsValue() as TData);
      if (result && result !== true) {
        form.setFields([
          {
            name: 'additionalValidation',
            errors: [result.message],
          },
        ]);
      } else {
        form.setFields([{ name: 'additionalValidation', errors: [] }]);
      }
    }, 100);
  }

  return (
    <>
      <FormItem name="additionalValidation" hidden />
      {!!errors.length && <Alert type="error" title={errors[0]} />}
    </>
  );
}

function haveValuesChanged(oldValues: unknown[], newValues: unknown[]) {
  if (oldValues.length !== newValues.length) {
    return true;
  }
  for (let i = 0; i < oldValues.length; i++) {
    if (oldValues[i] !== newValues[i]) {
      return true;
    }
  }
  return false;
}
